/**
 * App-level theme context — dark only.
 * Provides a `tokens` map used by components instead of hardcoded rgba values.
 *
 * Usage:
 *   const { tokens } = useAppTheme();
 *   // then reference tokens.bg, tokens.textPrimary, etc. in inline styles
 */
import React, { createContext, useContext, useMemo, useEffect, type ReactNode } from 'react';

// ── Token map ──────────────────────────────────────────────────────────────────

export interface ThemeTokens {
  // Backgrounds
  bg: string;
  bgHeader: string;
  bgCard: string;
  bgCardSubtle: string;
  bgInput: string;
  bgHover: string;
  bgOverlay: string;
  bgButtonSecondary: string;
  bgSettingsInner: string;
  bgDivider: string;

  // Text
  textPrimary: string;
  textMuted: string;
  textVeryMuted: string;
  textPlaceholder: string;

  // Borders
  border: string;
  borderSubtle: string;
  borderStrong: string;

  // Accent (purple)
  accent: string;
  accentMuted: string;
  accentHover: string;

  // Shimmer / skeleton
  shimmerFrom: string;
  shimmerTo: string;

  // Search overlay
  bgSearchPanel: string;

  // Notifications drawer
  bgNotifDrawer: string;
  bgNotifUnread: string;

  // Nav / bottom bar
  bgNav: string;
  navActiveColor: string;
  navActiveFg: string;
  navInactiveFg: string;
  navGradient: string;

  // Admin context menu
  bgAdminMenu: string;
}

const DARK_TOKENS: ThemeTokens = {
  bg: '#0a0a0f',
  bgHeader: 'rgba(10,10,15,0.95)',
  bgCard: 'rgba(255,255,255,0.04)',
  bgCardSubtle: 'rgba(255,255,255,0.06)',
  bgInput: 'rgba(255,255,255,0.06)',
  bgHover: 'rgba(255,255,255,0.05)',
  bgOverlay: 'rgba(0,0,0,0.65)',
  bgButtonSecondary: 'rgba(255,255,255,0.06)',
  bgSettingsInner: 'rgba(0,0,0,0.15)',
  bgDivider: 'rgba(255,255,255,0.06)',

  textPrimary: '#f0f0f5',
  textMuted: 'rgba(240,240,245,0.55)',
  textVeryMuted: 'rgba(240,240,245,0.35)',
  textPlaceholder: 'rgba(240,240,245,0.3)',

  border: 'rgba(255,255,255,0.08)',
  borderSubtle: 'rgba(255,255,255,0.06)',
  borderStrong: 'rgba(255,255,255,0.15)',

  accent: '#a78bfa',
  accentMuted: 'rgba(167,139,250,0.18)',
  accentHover: '#c4b5fd',

  shimmerFrom: '#070712',
  shimmerTo: '#10101e',

  bgSearchPanel: '#0B0A0F',
  bgNotifDrawer: '#0B0A0F',
  bgNotifUnread: 'rgba(167,139,250,0.06)',

  bgNav: 'rgba(7,8,15,0.4)',
  navActiveColor: '#46f5e0',
  navActiveFg: '#0a0a0f',
  navInactiveFg: 'rgba(240,240,245,0.7)',
  navGradient: 'linear-gradient(to top, #0B0A0F 0%, rgba(11,10,15,0.85) 30%, rgba(11,10,15,0.5) 60%, rgba(11,10,15,0) 100%)',

  bgAdminMenu: '#1a1a2e',
};

// ── Context ────────────────────────────────────────────────────────────────────

interface AppThemeContextValue {
  tokens: ThemeTokens;
}

const AppThemeContext = createContext<AppThemeContextValue | null>(null);

export function AppThemeProvider({ children }: { children: ReactNode }) {
  const tokens = useMemo<ThemeTokens>(() => DARK_TOKENS, []);

  useEffect(() => {
    document.body.style.background = tokens.bg;
    document.body.style.color = tokens.textPrimary;
  }, [tokens]);

  return (
    <AppThemeContext.Provider value={{ tokens }}>
      {children}
    </AppThemeContext.Provider>
  );
}

export function useAppTheme(): AppThemeContextValue {
  const ctx = useContext(AppThemeContext);
  if (!ctx) throw new Error('useAppTheme must be used within AppThemeProvider');
  return ctx;
}
